import customersApi from '@/services/customers.service'
import type {
    CustomerCreateModel,
    CustomerModel,
    CustomerUpdateModel
} from '@/types/models/CustomerModel'
import {
    EHandler,
    EPaginationOptions,
    type PaginationDataType,
    type PaginationType
} from '@rfc/rfc-components-library/types'
import { getPageSize } from '@rfc/rfc-components-library/utils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import { ECustomerActions, ECustomerMutations, type CustomerState } from './CustomerStoreTypes'
import { EAlertActions, EStoreGlobalModules } from '@rfc/rfc-components-library/store'
import i18n from '../../locales/i18n'

const state: Ref<PaginationDataType<CustomerState>> = ref({
    data: {
        customers: []
    },

    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    loading: false
})

const getters = {
    getCustomers: () => state.value.data?.customer,
    loading: () => state.value.loading
}

const actions = {
    async [ECustomerActions.FETCH_CUSTOMERS](
        { commit }: { commit: Commit },
        params?: Partial<PaginationType>
    ) {
        commit(ECustomerMutations.SET_LOADING, true)
        return customersApi
            .getCustomers(params)
            .then(data => {
                commit(ECustomerMutations.SET_CUSTOMER, data.data)
                commit(ECustomerMutations.SET_PAGINATION, data)
                return data
            })
            .finally(() => {
                commit(ECustomerMutations.SET_LOADING, false)
            })
    },

    async [ECustomerActions.FETCH_CUSTOMER](
        { commit }: { commit: Commit },
        { id }: { id: number }
    ) {
        commit(ECustomerMutations.SET_LOADING, true)
        return await customersApi
            .getCustomer(id)
            .then(res => {
                commit(ECustomerMutations.SET_CUSTOMER, res.data)
                return res.data
            })
            .finally(() => commit(ECustomerMutations.SET_LOADING, false))
    },

    async [ECustomerActions.CREATE_CUSTOMER](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        data: CustomerCreateModel
    ) {
        commit(ECustomerMutations.SET_LOADING, true)

        return customersApi
            .createCustomer(data)
            .then(res => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: ECustomerActions.CREATE_CUSTOMER
                })
                return res.data
            })
            .finally(() => commit(ECustomerMutations.SET_LOADING, false))
    },

    async [ECustomerActions.UPDATE_CUSTOMER](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, data }: { id: number; data: CustomerUpdateModel }
    ) {
        commit(ECustomerMutations.SET_LOADING, true)
        return customersApi
            .updateCustomer(id, data)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: ECustomerActions.UPDATE_CUSTOMER
                })
            )
            .finally(() => {
                commit(ECustomerMutations.SET_LOADING, false)
            })
    },

    async [ECustomerActions.DELETE_CUSTOMER](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, name }: { id: number; name: string }
    ) {
        await customersApi.deleteCustomer(id)
        commit(ECustomerMutations.REMOVE_CUSTOMER, id)
        return customersApi
            .deleteCustomer(id)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name,
                    key: ECustomerActions.DELETE_CUSTOMER
                })
            )
            .finally(() => commit(ECustomerMutations.SET_LOADING, false))
    },

    [ECustomerActions.RESET_CUSTOMER]({ commit }: { commit: Commit }) {
        commit(ECustomerMutations.RESET_CUSTOMER)
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreGlobalModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`customers.api.success.${key}`, { name })
            },
            { root: true }
        )
    }
}

const mutations = {
    [ECustomerMutations.SET_CUSTOMERS](_: CustomerState, customers: CustomerModel[]) {
        state.value.data!.customers = customers
    },
    [ECustomerMutations.SET_CUSTOMER](_: CustomerState, customer: CustomerModel) {
        state.value.data!.customer = customer
    },
    [ECustomerMutations.SET_LOADING](_: CustomerState, loading: boolean) {
        state.value.loading = loading
    },
    [ECustomerMutations.SET_PAGINATION](_: CustomerState, data: PaginationDataType<CustomerModel>) {
        state.value.current_page = data.current_page
        state.value.total = data.total
        state.value.last_page = data.last_page
        state.value.to = data.to
        state.value.from = data.from
    },
    [ECustomerMutations.REMOVE_CUSTOMER](_: CustomerState, id: number) {
        state.value.data!.customers =
            state.value.data?.customers?.filter(item => item.id !== id) ?? []
    },
    [ECustomerMutations.RESET_CUSTOMER]() {
        state.value.data!.customer = undefined
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
